import { ref, onMounted, onUnmounted } from 'vue'

export function useTabManager() {
  const TAB_REGISTRY_KEY = 'activeTabsRegistry'
  const TAB_HEARTBEAT_KEY = 'tabHeartbeat'
  const TAB_CLOSE_DETECTION_KEY = 'tabCloseDetection'
  
  const tabId = generateUniqueTabId()
  const isLastTab = ref(false)
  
  let heartbeatInterval = null
  let cleanupInterval = null
  let isActive = true
  
  // Gerar ID único para a aba
  function generateUniqueTabId() {
    return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }
  
  // Registrar esta aba
  const registerTab = () => {
    try {
      const registry = getTabRegistry()
      registry[tabId] = {
        timestamp: Date.now(),
        userAgent: navigator.userAgent.slice(0, 50), // Identifica o browser
        url: window.location.href
      }
      localStorage.setItem(TAB_REGISTRY_KEY, JSON.stringify(registry))
      updateTabCount()
    } catch (error) {
      console.warn('Erro ao registrar aba:', error)
    }
  }
  
  // Remover esta aba do registro
  const unregisterTab = () => {
    try {
      const registry = getTabRegistry()
      delete registry[tabId]
      localStorage.setItem(TAB_REGISTRY_KEY, JSON.stringify(registry))
      updateTabCount()
    } catch (error) {
      console.warn('Erro ao remover aba do registro:', error)
    }
  }
  
  // Obter registro de abas
  const getTabRegistry = () => {
    try {
      const registry = localStorage.getItem(TAB_REGISTRY_KEY)
      return registry ? JSON.parse(registry) : {}
    } catch (error) {
      return {}
    }
  }
  
  // Limpar abas expiradas do registro
  const cleanupExpiredTabs = () => {
    try {
      const registry = getTabRegistry()
      const now = Date.now()
      const HEARTBEAT_TIMEOUT = 15000 // 15 segundos
      
      let hasChanges = false
      Object.keys(registry).forEach(id => {
        if (now - registry[id].timestamp > HEARTBEAT_TIMEOUT) {
          delete registry[id]
          hasChanges = true
        }
      })
      
      if (hasChanges) {
        localStorage.setItem(TAB_REGISTRY_KEY, JSON.stringify(registry))
        updateTabCount()
      }
    } catch (error) {
      console.warn('Erro ao limpar abas expiradas:', error)
    }
  }
  
  // Atualizar contagem de abas ativas
  const updateTabCount = () => {
    try {
      const registry = getTabRegistry()
      const activeTabCount = Object.keys(registry).length
      
      isLastTab.value = activeTabCount <= 1
      
      // Notificar mudança na contagem
      localStorage.setItem('activeTabCount', activeTabCount.toString())
      
      return activeTabCount
    } catch (error) {
      isLastTab.value = true
      return 1
    }
  }
  
  // Enviar heartbeat desta aba
  const sendHeartbeat = () => {
    if (!isActive) return
    
    try {
      const registry = getTabRegistry()
      if (registry[tabId]) {
        registry[tabId].timestamp = Date.now()
        localStorage.setItem(TAB_REGISTRY_KEY, JSON.stringify(registry))
        
        // Também atualizar heartbeat global
        localStorage.setItem(TAB_HEARTBEAT_KEY, JSON.stringify({
          tabId: tabId,
          timestamp: Date.now()
        }))
      }
    } catch (error) {
      console.warn('Erro ao enviar heartbeat:', error)
    }
  }
  
  // Detectar quando a aba está sendo fechada
  const handleBeforeUnload = (event) => {
    try {
      isActive = false
      
      // Remover esta aba do registro
      unregisterTab()
      
      // Verificar se esta era a última aba
      const activeTabCount = updateTabCount()
      
      if (activeTabCount === 0) {
        // Esta era a última aba - sinalizar fechamento completo
        localStorage.setItem(TAB_CLOSE_DETECTION_KEY, JSON.stringify({
          timestamp: Date.now(),
          reason: 'allTabsClosed',
          lastTabId: tabId
        }))
      }
    } catch (error) {
      console.warn('Erro no beforeunload:', error)
    }
  }
  
  // Detectar mudança de visibilidade da página
  const handleVisibilityChange = () => {
    if (document.hidden) {
      isActive = false
    } else {
      isActive = true
      // Reregistrar a aba quando ela voltar ao foco
      registerTab()
    }
  }
  
  // Verificar se houve fechamento de todas as abas
  const checkForTabClosure = () => {
    try {
      const closeDetection = localStorage.getItem(TAB_CLOSE_DETECTION_KEY)
      if (!closeDetection) return null
      
      const data = JSON.parse(closeDetection)
      const timeSinceClosure = Date.now() - data.timestamp
      
      // Se foi há pouco tempo (menos de 5 minutos), considerar fechamento válido
      // Aumentado de 30 segundos para 5 minutos para cobrir casos de ausência prolongada
      if (timeSinceClosure < 300000) { // 5 minutos = 300000ms
        // Limpar o sinal
        localStorage.removeItem(TAB_CLOSE_DETECTION_KEY)
        return {
          wasAllTabsClosed: true,
          timeSinceClosure: timeSinceClosure,
          reason: data.reason
        }
      }
      
      // Se passou muito tempo, limpar o sinal antigo
      localStorage.removeItem(TAB_CLOSE_DETECTION_KEY)
      return null
    } catch (error) {
      return null
    }
  }
  
  // Listener para mudanças no localStorage
  const handleStorageChange = (event) => {
    if (event.key === TAB_REGISTRY_KEY) {
      updateTabCount()
    }
  }
  
  // Inicializar gerenciamento de abas
  const init = () => {
    try {
      // Verificar fechamento anterior
      const closureInfo = checkForTabClosure()
      
      // Registrar esta aba
      registerTab()
      
      // Configurar heartbeat
      heartbeatInterval = setInterval(() => {
        sendHeartbeat()
      }, 5000)
      
      // Configurar limpeza de abas expiradas
      cleanupInterval = setInterval(() => {
        cleanupExpiredTabs()
      }, 10000)
      
      // Listeners
      window.addEventListener('beforeunload', handleBeforeUnload)
      document.addEventListener('visibilitychange', handleVisibilityChange)
      window.addEventListener('storage', handleStorageChange)
      
      return closureInfo
    } catch (error) {
      console.warn('Erro ao inicializar gerenciamento de abas:', error)
      return null
    }
  }
  
  // Limpar recursos
  const cleanup = () => {
    try {
      isActive = false
      
      // Limpar intervals
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval)
      }
      if (cleanupInterval) {
        clearInterval(cleanupInterval)
      }
      
      // Remover listeners
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('storage', handleStorageChange)
      
      // Remover aba do registro
      unregisterTab()
    } catch (error) {
      console.warn('Erro na limpeza do gerenciamento de abas:', error)
    }
  }
  
  // Lifecycle hooks
  onMounted(() => {
    init()
  })
  
  onUnmounted(() => {
    cleanup()
  })
  
  return {
    tabId,
    isLastTab,
    init,
    cleanup,
    checkForTabClosure,
    getActiveTabCount: () => Object.keys(getTabRegistry()).length
  }
} 